import { useState, useEffect, useRef } from 'react';

/**
 * Detects whether the fixed-position chat FAB overlaps a dark or light
 * landing-page section by checking `data-saxa-bg` attributes on scroll.
 *
 * Returns:
 *  - `true`  → avatar is over a dark section (light-mode only)
 *  - `false` → avatar is over a light section (light-mode only)
 *  - `undefined` → dark-mode page or no tagged section found (fall back to theme)
 */
export function useBackdropDark(
  anchorRef: React.RefObject<HTMLElement | null>,
): boolean | undefined {
  const [isDark, setIsDark] = useState<boolean | undefined>(undefined);
  const ticking = useRef(false);

  useEffect(() => {
    function detect() {
      // In dark mode every section renders dark — let theme detection handle it
      if (document.documentElement.classList.contains('dark')) {
        setIsDark(undefined);
        return;
      }

      const el = anchorRef.current;
      if (!el) {
        setIsDark(undefined);
        return;
      }

      // Vertical centre of the FAB button
      const rect = el.getBoundingClientRect();
      const cy = rect.top + rect.height / 2;

      const sections = document.querySelectorAll<HTMLElement>('[data-saxa-bg]');
      for (const section of sections) {
        const sRect = section.getBoundingClientRect();
        if (cy >= sRect.top && cy < sRect.bottom) {
          setIsDark(section.dataset.saxaBg === 'dark');
          return;
        }
      }

      // No tagged section at this position
      setIsDark(undefined);
    }

    function onScroll() {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(() => {
          detect();
          ticking.current = false;
        });
      }
    }

    detect();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [anchorRef]);

  return isDark;
}
